import { useState, type CSSProperties } from "react";
import { api, setBearerToken } from "../lib/api";
import { WebAppAuthResponse } from "../types/api";
import { hapticImpact, hapticNotification } from "../lib/telegram";
import { ElementEmblem } from "../components/ElementEmblem";
import { ElementalBackdrop } from "../components/ElementalBackdrop";
import { HeroRarityColor, HeroRarityRu } from "../types/heroes";

interface Props {
  initData: string;
  onRegistered: (data: {
    playerId: string;
    worldId: number;
    telegramId: number;
    displayName: string | null;
  }) => void;
}

interface UnitSlot {
  role: string;
  name: string;
  icon: string;
}

interface HeroSpotlight {
  name: string;
  rarity: number;
  // Painted portrait under webapp/public/icons/heroes/. Composition is 4:3
  // landscape; rendered with object-cover in a square tile.
  portrait: string;
}

interface ElementOption {
  id: number;
  name: string;
  blurb: string;
  long: string;
  glow: string;
  accent: string;
  button: string;
  link: string;
  lore: string;
  strengths: string[];
  units: UnitSlot[];
  heroes: HeroSpotlight[];
  totalHeroCount: number;
  signatureBuilding: { name: string; icon: string; description: string };
  playstyle: string;
}

const RarityGradient: Record<number, string> = {
  3: "linear-gradient(135deg, #f4b400 0%, #a06b00 50%, #5c3a00 100%)",
  2: "linear-gradient(135deg, #b48dff 0%, #6a3fb8 50%, #2e1a5e 100%)",
  1: "linear-gradient(135deg, #5ac8ff 0%, #2e7dd9 50%, #143b6e 100%)",
  0: "linear-gradient(135deg, #888888 0%, #555555 50%, #2a2a2a 100%)",
};

// Mirrors shared/ElementalRealms.Shared/Runtime/Combat/ElementMatchupTable.cs.
// Rows = attacker, cols = defender. Keep in sync with that file.
const MatchupTable: number[][] = [
  /* Fire  */ [1.0, 0.85, 1.2, 1.0],
  /* Water */ [1.2, 1.0, 1.0, 0.85],
  /* Air   */ [0.85, 1.0, 1.0, 1.2],
  /* Earth */ [1.0, 1.2, 0.85, 1.0],
];

const ElementNames = ["Огонь", "Вода", "Воздух", "Земля"];

type MatchupTone = "advantage" | "neutral" | "vulnerable";

interface Matchup {
  opponentId: number;
  opponentName: string;
  attack: number;
  defense: number;
  label: string;
  tone: MatchupTone;
}

function computeMatchups(myId: number): Matchup[] {
  const out: Matchup[] = [];
  for (let opp = 0; opp < 4; opp++) {
    if (opp === myId) continue;
    const myAtk = MatchupTable[myId][opp];
    const theirAtk = MatchupTable[opp][myId];
    const ratio = myAtk / theirAtk;
    let tone: MatchupTone;
    let label: string;
    if (ratio > 1.1) {
      tone = "advantage";
      label = "Ты сильнее";
    } else if (ratio > 0.9) {
      tone = "neutral";
      label = "Поровну";
    } else {
      tone = "vulnerable";
      label = "Ты слабее";
    }
    out.push({
      opponentId: opp,
      opponentName: ElementNames[opp],
      attack: myAtk,
      defense: theirAtk,
      label,
      tone,
    });
  }
  const order: Record<MatchupTone, number> = {
    advantage: 0,
    neutral: 1,
    vulnerable: 2,
  };
  out.sort((a, b) => order[a.tone] - order[b.tone]);
  return out;
}

// Tone backgrounds + text colors come from CSS vars set in index.css per theme
// (light vs dark variant). Using arbitrary values via [var(...)] keeps the
// chip readable on both themes without duplicating className strings.
const ToneClass: Record<MatchupTone, string> = {
  advantage:
    "bg-[var(--app-tone-advantage-bg)] text-[color:var(--app-tone-advantage-text)] border-[color:var(--app-tone-advantage-border)]",
  neutral:
    "bg-[var(--app-tone-neutral-bg)] text-[color:var(--app-tone-neutral-text)] border-[color:var(--app-tone-neutral-border)]",
  vulnerable:
    "bg-[var(--app-tone-vulnerable-bg)] text-[color:var(--app-tone-vulnerable-text)] border-[color:var(--app-tone-vulnerable-border)]",
};

const Elements: ElementOption[] = [
  {
    id: 0,
    name: "Огонь",
    blurb: "Сильно бьёт · Атакует первым",
    long: "Армия, которая бьёт больно. Главный воин — феникс, огненная птица.",
    glow: "rgba(255, 120, 60, 0.55)",
    accent: "#ff8a4d",
    button: "#d94326",
    link: "#ff8a4d",
    lore:
      "Стихия огня — это сила атаки. Твои воины наносят больше всех урона в бою, но и сами легко падают, если враг крепкий. Главный воин — феникс, который возрождается из пепла. Подходит тем, кто любит нападать.",
    strengths: ["Сильно бьёт в атаке", "Быстро набирает темп", "Опасен в наступлении"],
    units: [
      { role: "Пехота", name: "Лавовый страж", icon: "/icons/units/fire-guardian.png" },
      { role: "Лучники", name: "Огненный лучник", icon: "/icons/units/fire-archer.png" },
      { role: "Маги", name: "Пиромант", icon: "/icons/units/fire-pyromancer.png" },
      { role: "Особые", name: "Феникс", icon: "/icons/units/fire-phoenix.png" },
    ],
    heroes: [
      { name: "Драконоборец",      rarity: 3, portrait: "/icons/heroes/fire-dragonslayer.jpg" },
      { name: "Огненный Лорд",    rarity: 2, portrait: "/icons/heroes/fire-firelord.jpg" },
      { name: "Полководец огня",  rarity: 2, portrait: "/icons/heroes/fire-firecommander.jpg" },
    ],
    totalHeroCount: 10,
    signatureBuilding: {
      name: "Пепельный алтарь",
      icon: "/icons/buildings/signature-fire.png",
      description: "Здание, в котором рождается феникс — главный воин Огня.",
    },
    playstyle:
      "Нападай первым и бей сильно. Сильнее всех в атаке, но в обороне страдает.",
  },
  {
    id: 1,
    name: "Вода",
    blurb: "Лечит воинов · Крепкая оборона",
    long: "Армия, которая выживает. Русалка лечит раненых после боя.",
    glow: "rgba(70, 170, 255, 0.55)",
    accent: "#5ac8ff",
    button: "#2e7dd9",
    link: "#5ac8ff",
    lore:
      "Стихия воды — это выживание. Твои воины крепче всех и дольше всех держатся в бою. Русалка-целитель возвращает раненых обратно в строй. Бьёт не так сильно, как Огонь, но и не падает. Подходит тем, кто любит обороняться и побеждать измором.",
    strengths: ["Возвращает раненых в строй", "Самые крепкие воины", "Не падает под натиском"],
    units: [
      { role: "Пехота", name: "Коралловый голем", icon: "/icons/units/water-golem.png" },
      { role: "Лучники", name: "Ледяной лучник", icon: "/icons/units/water-archer.png" },
      { role: "Маги", name: "Криомант", icon: "/icons/units/water-cryomancer.png" },
      { role: "Особые", name: "Русалка-лекарь", icon: "/icons/units/water-mermaid.png" },
    ],
    heroes: [
      { name: "Хранитель глубин", rarity: 3, portrait: "/icons/heroes/water-deepkeeper.jpg" },
      { name: "Морской царь",     rarity: 2, portrait: "/icons/heroes/water-seaking.jpg" },
      { name: "Адмирал",          rarity: 2, portrait: "/icons/heroes/water-admiral.jpg" },
    ],
    totalHeroCount: 10,
    signatureBuilding: {
      name: "Священное озеро",
      icon: "/icons/buildings/signature-water.png",
      description: "Озеро, где живут русалки. Они лечат твоих раненых воинов.",
    },
    playstyle:
      "Не торопись. Защищайся, лечи раненых, побеждай тем, что не падаешь.",
  },
  {
    id: 2,
    name: "Воздух",
    blurb: "Быстрая армия · Удар с неба",
    long: "Армия, которая бьёт первой. Грифон — летающий воин стихии.",
    glow: "rgba(180, 140, 255, 0.55)",
    accent: "#b48dff",
    button: "#7c4dff",
    link: "#b48dff",
    lore:
      "Стихия воздуха — это скорость. Твои воины ударяют раньше других и наносят сильный урон. Не такие крепкие, как Земля или Вода, но опасные за счёт быстроты. Главный воин — грифон, летающий зверь с молниями. Подходит тем, кто любит атаковать неожиданно.",
    strengths: ["Высокий урон у всех воинов", "Бьёт первым", "Неожиданные атаки"],
    units: [
      { role: "Пехота", name: "Гарпия", icon: "/icons/units/air-harpy.png" },
      { role: "Лучники", name: "Штормовой лучник", icon: "/icons/units/air-archer.png" },
      { role: "Маги", name: "Аэромант", icon: "/icons/units/air-aero.png" },
      { role: "Особые", name: "Грифон", icon: "/icons/units/air-griffin.png" },
    ],
    heroes: [
      { name: "Архимаг бури",   rarity: 3, portrait: "/icons/heroes/air-stormarchmage.jpg" },
      { name: "Воздушный лорд", rarity: 2, portrait: "/icons/heroes/air-airlord.jpg" },
      { name: "Пилот",          rarity: 2, portrait: "/icons/heroes/air-pilot.jpg" },
    ],
    totalHeroCount: 10,
    signatureBuilding: {
      name: "Чертог бурь",
      icon: "/icons/buildings/signature-air.png",
      description: "Здание на горной вершине, где живут грифоны.",
    },
    playstyle:
      "Бей быстро и точно. Воины менее крепкие, но они бьют сильнее всех и первыми.",
  },
  {
    id: 3,
    name: "Земля",
    blurb: "Самая крепкая армия · Танк",
    long: "Армия, которую трудно сломить. Мифриловый титан — главный защитник.",
    glow: "rgba(120, 200, 110, 0.55)",
    accent: "#7ed46d",
    button: "#3f9c3f",
    link: "#7ed46d",
    lore:
      "Стихия земли — это прочность. У твоих воинов самый большой запас здоровья и брони. Они медленные и не очень больно бьют, но их трудно убить. Главный воин — мифриловый титан, гигантский каменный воин. Подходит тем, кто любит выдерживать удар и побеждать измором.",
    strengths: ["Самое большое здоровье", "Крепкая броня", "Трудно убить"],
    units: [
      { role: "Пехота", name: "Каменный голем", icon: "/icons/units/earth-golem.png" },
      { role: "Лучники", name: "Землетрясец", icon: "/icons/units/earth-quaker.png" },
      { role: "Маги", name: "Геомант", icon: "/icons/units/earth-geo.png" },
      { role: "Особые", name: "Мифриловый титан", icon: "/icons/units/earth-titan.png" },
    ],
    heroes: [
      { name: "Мать земли",     rarity: 3, portrait: "/icons/heroes/earth-earthmother.jpg" },
      { name: "Каменный страж", rarity: 2, portrait: "/icons/heroes/earth-stoneguard.jpg" },
      { name: "Бергарь",        rarity: 2, portrait: "/icons/heroes/earth-mountaineer.jpg" },
    ],
    totalHeroCount: 10,
    signatureBuilding: {
      name: "Каменный курган",
      icon: "/icons/buildings/signature-earth.png",
      description: "Здание, где создают каменных титанов — главных воинов Земли.",
    },
    playstyle:
      "Будь крепостью. Враги бьют — ты стоишь. Когда они выдохнутся, ты ещё на ногах.",
  },
];

export function ElementPicker({ initData, onRegistered }: Props) {
  const [previewing, setPreviewing] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (element: number) => {
    setPending(true);
    setError(null);
    hapticImpact("medium");
    try {
      const res = await api.post<WebAppAuthResponse>("/webapp/register", {
        initData,
        element,
      });
      const body = res.data;
      if (!body.isRegistered || !body.token) {
        setError("Регистрация не удалась. Попробуй ещё раз.");
        setPending(false);
        return;
      }
      setBearerToken(body.token);
      hapticNotification("success");
      onRegistered({
        playerId: body.playerId,
        worldId: body.worldId,
        telegramId: body.telegramId,
        displayName: body.firstName,
      });
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ??
            null)
          : null;
      setError(msg ?? "Не получилось связаться с сервером.");
      setPending(false);
    }
  };

  if (previewing !== null) {
    const opt = Elements[previewing];
    return (
      <ElementDetail
        opt={opt}
        pending={pending}
        error={error}
        onAccept={() => submit(opt.id)}
        onBack={() => {
          hapticImpact("light");
          setPreviewing(null);
          setError(null);
        }}
      />
    );
  }

  return (
    <div className="relative flex h-full flex-col overflow-y-auto">
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('/icons/splash.png')",
          opacity: 0.35,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, var(--app-splash-grad-top) 0%, var(--app-splash-grad-bottom) 100%)",
        }}
        aria-hidden
      />

      <div className="relative z-10 px-4 py-5">
        {/* Header text sits directly on the dark painterly splash, so it gets
            explicit white tones — otherwise on light Telegram theme the
            inherited near-black text would disappear into the dark image. */}
        <div className="mb-5 text-center text-white drop-shadow-md">
          <div className="text-2xl font-semibold">Добро пожаловать</div>
          <div className="mt-1 text-sm text-white/80">
            Выбери одну из четырёх стихий — от неё зависят твои воины и герои.
            Тапни по карточке, чтобы прочитать подробности. Поменять стихию позже
            нельзя.
          </div>
        </div>

        <div className="space-y-3">
          {Elements.map((e, idx) => (
            <button
              key={e.id}
              type="button"
              onClick={() => {
                hapticImpact("light");
                setPreviewing(e.id);
              }}
              style={
                {
                  "--card-glow": e.glow,
                  "--enter-delay": `${idx * 120}ms`,
                } as CSSProperties
              }
              className="animate-picker-enter flex w-full items-center gap-3 rounded-xl bg-tg-secondaryBg/85 p-3 text-left backdrop-blur transition-transform active:scale-95"
            >
              <ElementEmblem
                element={e.id}
                size={64}
                className="shrink-0 animate-emblem-glow"
              />
              <div className="min-w-0 flex-1">
                <div className="text-lg font-semibold">{e.name}</div>
                <div className="text-xs text-tg-accent">{e.blurb}</div>
                <div className="mt-1 text-xs text-tg-hint">{e.long}</div>
              </div>
              <div className="text-tg-hint">›</div>
            </button>
          ))}
        </div>

        <div className="mt-6 text-center text-xs text-white/75 drop-shadow">
          На 3 дня у тебя будет щит новичка — пока он действует, никто на тебя
          не нападёт. Спокойно построй замок и собери первую армию.
        </div>
      </div>
    </div>
  );
}

interface DetailProps {
  opt: ElementOption;
  pending: boolean;
  error: string | null;
  onAccept: () => void;
  onBack: () => void;
}

function ElementDetail({ opt, pending, error, onAccept, onBack }: DetailProps) {
  const themedStyle: CSSProperties = {
    "--tg-theme-accent-text-color": opt.accent,
    "--tg-theme-button-color": opt.button,
    "--tg-theme-link-color": opt.link,
    "--element-glow": opt.glow,
  } as CSSProperties;

  return (
    <div className="relative flex h-full flex-col" style={themedStyle}>
      <ElementalBackdrop element={opt.id} />

      <div className="relative z-10 flex h-full flex-col overflow-y-auto">
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={onBack}
            disabled={pending}
            className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm disabled:opacity-50"
          >
            ←
          </button>
          <div className="font-medium">{opt.name}</div>
        </div>

        <div className="animate-fade-in-up flex-1 px-3 pb-3">
          <div className="mb-4 mt-2 text-center">
            <ElementEmblem
              element={opt.id}
              size={140}
              className="animate-emblem-glow"
            />
            <div className="mt-2 text-3xl font-bold drop-shadow">{opt.name}</div>
            <div className="mt-1 text-sm text-tg-accent">{opt.blurb}</div>
          </div>

          <div className="space-y-3">
            <DetailSection title="О стихии">
              <div className="text-sm leading-relaxed">{opt.lore}</div>
            </DetailSection>

            <DetailSection title="Что эта стихия умеет">
              <div className="flex flex-wrap gap-1.5">
                {opt.strengths.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-tg-bg/70 px-2 py-0.5 text-xs text-tg-accent"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </DetailSection>

            <DetailSection title="Кого побеждает, кому проигрывает">
              <div className="space-y-2">
                {computeMatchups(opt.id).map((m) => (
                  <div
                    key={m.opponentId}
                    className="flex items-center gap-3 rounded-lg bg-tg-bg/60 p-2"
                  >
                    <ElementEmblem
                      element={m.opponentId}
                      size={36}
                      className="shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium">{m.opponentName}</div>
                        <span
                          className={`rounded-full border px-1.5 py-0 text-[10px] uppercase tracking-wide ${ToneClass[m.tone]}`}
                        >
                          {m.label}
                        </span>
                      </div>
                      <div className="text-xs text-tg-hint tabular-nums">
                        Твой удар ×{m.attack.toFixed(2)} · Его удар ×
                        {m.defense.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </DetailSection>

            <DetailSection title="Воины">
              <div className="grid grid-cols-2 gap-2">
                {opt.units.map((u) => (
                  <div
                    key={u.name}
                    className="flex items-center gap-2 rounded-lg bg-tg-bg/60 p-2"
                  >
                    <img
                      src={u.icon}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-md object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wide text-tg-hint">
                        {u.role}
                      </div>
                      <div className="text-sm leading-tight">{u.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </DetailSection>

            <DetailSection
              title="Главные герои стихии"
              hint={
                opt.totalHeroCount > opt.heroes.length
                  ? `всего ${opt.totalHeroCount}`
                  : undefined
              }
            >
              <div className="grid grid-cols-3 gap-2">
                {opt.heroes.map((h) => (
                  <div key={h.name} className="flex flex-col items-center">
                    <div
                      className="relative aspect-square w-full overflow-hidden rounded-lg"
                      style={{
                        background: RarityGradient[h.rarity],
                        // Rarity-tinted frame: ring color reuses the gradient's
                        // top stop so Epic stays violet, Legendary stays gold.
                        boxShadow: `0 0 0 1px rgba(0,0,0,0.4), 0 0 14px -2px ${
                          h.rarity === 3 ? "rgba(244,180,0,0.55)" :
                          h.rarity === 2 ? "rgba(180,141,255,0.55)" :
                          h.rarity === 1 ? "rgba(90,200,255,0.45)" :
                                            "rgba(180,180,180,0.30)"
                        }`,
                      }}
                    >
                      <img
                        src={h.portrait}
                        alt={h.name}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                        draggable={false}
                      />
                    </div>
                    <div className="mt-1 line-clamp-2 text-center text-[11px] font-medium leading-tight">
                      {h.name}
                    </div>
                    <div
                      className={`text-[10px] uppercase tracking-wide ${HeroRarityColor[h.rarity]}`}
                    >
                      {HeroRarityRu[h.rarity]}
                    </div>
                  </div>
                ))}
              </div>
            </DetailSection>

            <DetailSection title="Особое здание стихии">
              <div className="flex items-center gap-3">
                <img
                  src={opt.signatureBuilding.icon}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0">
                  <div className="font-medium">{opt.signatureBuilding.name}</div>
                  <div className="text-xs text-tg-hint">
                    {opt.signatureBuilding.description}
                  </div>
                </div>
              </div>
            </DetailSection>

            <DetailSection title="Как играть">
              <div className="text-sm italic text-tg-hint">«{opt.playstyle}»</div>
            </DetailSection>

            {error && (
              <div className="rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 z-20 grid grid-cols-[1fr_2fr] gap-2 border-t border-tg-secondaryBg/40 bg-tg-bg/85 px-3 py-3 backdrop-blur">
          <button
            type="button"
            disabled={pending}
            onClick={onBack}
            className="rounded-lg bg-tg-secondaryBg px-3 py-3 text-sm disabled:opacity-50"
          >
            Назад
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={onAccept}
            className="animate-element-pulse rounded-lg bg-tg-button px-3 py-3 font-medium text-tg-buttonText disabled:opacity-50"
          >
            {pending ? "Заключаем союз..." : "Принять силу"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="element-accent rounded-xl bg-tg-secondaryBg/85 p-3 backdrop-blur">
      <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs uppercase tracking-wide text-tg-hint">
        <span>{title}</span>
        {hint && <span className="text-tg-accent">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
